import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import api from 'api'
import styled from '@emotion/styled'
import { css } from '@emotion/css'
import Avatar from 'components/avatar'
import RouteLogo from 'components/route-logo'
import Button from 'components/button'
import AButton from 'components/a-button'
import DownloadModal from 'components/download-modal'
import Section from 'components/section'
import Accent from 'components/accent'
import { useTitle } from 'react-use'
import isMobileDevice from 'utils/isMobileDevice'
import VerticalCarousel from './vertical-carousel'

const Flex = styled.div`
  display: flex;
  flex-flow: row nowrap;
  height: 50vh;
  min-height: 164px;
  @media (max-width: 600px) {
    flex-direction: column-reverse;
    height: auto;
  }
`

const PagePadding = styled.div`
  padding: 0 20%;
  @media (max-width: 600px) {
    padding: 0px 8px;
  }
`

const WidthControlledImage = styled.img`
  border-radius: 8px;
  max-height: 100%;
  width: 100%;
  display: block;
  object-fit: scale-down;
`

const ImageWrapper = styled.div`
  flex-grow: 1;
`

const RouteBranding = styled.div`
  width: 100%;
  height: 64px;
  padding-top: 32px;
  display: inline-flex;
  justify-content: flex-start;
  @media (max-width: 600px) {
    justify-content: center;
  }
`

const Details = styled.div`
  margin-left: 32px;
  @media (max-width: 600px) {
    margin-bottom: 24px;
  }
`

const ProductTitle = styled.div`
  margin-top: 48px;
  font-weight: bold;
  font-size: 34px;
  @media (max-width: 600px) {
    margin-top: 16px;
  }
`

const ProductPrice = styled.div`
  font-size: 16px;
`

const marginButton = css`
  margin-top: 48px;
  @media (max-width: 600px) {
    margin-top: 16px;
    margin-right: 8px;
    float: right;
  }
`

const Masonry = styled.div`
  margin-top: 24px;
  column-count: 4;
  column-width: 200px;
  column-gap: 8px;
  column-fill: balance;
  @media (max-width: 600px) {
    column-width: 180px;
  }
  @media (max-width: 300px) {
    column-width: 140px;
  }
`

const SuggestionImage = styled.img`
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
`

const Relative = styled.div`
  position: relative;
  max-height: 500px;
  overflow: hidden;
`

const Gradient = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 0.3),
    rgba(255, 255, 255, 1)
  );
  position: absolute;
  bottom: 0;
`

const CTA = styled.div`
  text-align: center;
  font-size: 34px;
  line-height: 36px;
  padding-bottom: 48px;
  padding-left: 25%;
  padding-right: 25%;
  font-weight: 700;
`

const Product = () => {
  const { productId } = useParams()
  const history = useHistory()
  const [product, setProduct] = useState()
  const [selectedImage, setSelectedImage] = useState(0)
  const [modalOpen, setModalOpen] = useState(false)
  const [recommendedProducts, setRecommendedProducts] = useState()
  const [oneLink, setOneLink] = useState()
  const isMobile = useMemo(() => isMobileDevice())

  useEffect(() => {
    const func = async () => {
      if (isMobile) {
        const link = await api.share.getOneLink('product', productId)
        setOneLink(link)
      }
    }
    func()
  }, [setOneLink, isMobile])

  useTitle(product?.name ? product.name : 'Discover Product')

  useEffect(async () => {
    let routedProduct
    try {
      routedProduct = await api.product.getProductById(productId)
      if (routedProduct) {
        setProduct(routedProduct)
        setRecommendedProducts(
          (await api.product.getMerchantProductsById(routedProduct.merchant_id, 12)).results,
        )
      }
    } catch (e) {
      history.replace('/product')
    }
  }, [productId])

  return (
    <PagePadding>
      <RouteBranding>
        <RouteLogo />
      </RouteBranding>
      <Flex>
        <VerticalCarousel
          images={product?.images.Records}
          selected={selectedImage}
          onChange={setSelectedImage}
        />
        <ImageWrapper>
          <WidthControlledImage src={product?.images.Records[selectedImage].url} alt="" />
        </ImageWrapper>
        <Details>
          {product && (
            <Avatar
              title={product.store_name}
              subtitle={product.vendor}
              url={product.store_logo}
              verified={!product.blacklisted}
            />
          )}

          {product && (
            <div>
              <ProductTitle>{product.name}</ProductTitle>
              <ProductPrice>{product.price}</ProductPrice>
              {isMobile && oneLink ? (
                <AButton css={marginButton} href={oneLink}>
                  Open In App
                </AButton>
              ) : (
                <Button css={marginButton} onClick={() => setModalOpen(true)}>
                  Open In App
                </Button>
              )}
            </div>
          )}
        </Details>
      </Flex>
      {recommendedProducts ? <Section title="Related Items" /> : null}
      <Relative>
        <Masonry>
          {recommendedProducts?.map((p) => (
            <div key={p.id}>
              <SuggestionImage src={p.images.Records[0]?.url} alt={p.name} />
            </div>
          ))}
        </Masonry>
        <Gradient />
      </Relative>
      <CTA>
        <p>
          <Accent>Discover</Accent>
          {' '}
          new brands and products in the Route app
        </p>
        {isMobile && oneLink ? (
          <AButton css={marginButton} href={oneLink}>
            Open In App
          </AButton>
        ) : (
          <Button css={marginButton} onClick={() => setModalOpen(true)}>
            Open In App
          </Button>
        )}
      </CTA>
      <DownloadModal
        isOpen={modalOpen}
        close={() => setModalOpen(false)}
        contentLabel="Download the Route App"
      />
    </PagePadding>
  )
}

export default Product
